import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ArrowLeft, ArrowRight } from "lucide-react";

type Slide = {
  title: React.ReactNode;
  subtitle: string;
  imageUrl: string;
  align: "left" | "center" | "right";
};

const slides: Slide[] = [
  {
    title: <>Integrated Staffing Solutions</>,
    subtitle:
      "Discover the potential of our Manpower & Specialized solutions and witness the transformation they can bring to your organization's efficiency and effectiveness.",
    imageUrl:
      "/assest/heroslider/1st%20Slider_1_11zon.webp",
    align: "left",
  },
  {
    title: (
      <>
        Your Path to Streamlined
        <br /> Operations Begins Here
      </>
    ),
    subtitle:
      "We drive your business success by connecting you with proficient personnel equipped with specialized training and refined skill sets for optimal job performance.",
    imageUrl:
      "/assest/heroslider/2nd%20Slider_2_11zon.webp",
    align: "center",
  },
  {
    title: (
      <>
        Empowering Success:
        <br /> Your Trusted Manpower Services Partner
      </>
    ),
    subtitle:
      "Our commitment lies in synergizing with clients to empower their growth and success.",
    imageUrl:
      "/assest/heroslider/3rd%20Slider_3_11zon.webp",
    align: "left",
  },
  {
    title: <>Building Teams, Creating Success Stories</>,
    subtitle:
      "Connecting talented professionals with outstanding opportunities across diverse industries for mutual growth and success.",
    imageUrl:
      "/assest/heroslider/4th%20Slider_4_11zon.webp",
    align: "center",
  },
  {
    title: (
      <>
        Delivering Excellence,
        <br /> Every Time
      </>
    ),
    subtitle:
      "Providing skilled professionals for logistics and delivery services to ensure your operations run smoothly and efficiently.",
    imageUrl:
      "/assest/heroslider/5th%20Slider_5_11zon.webp",
    align: "left",
  },
];

const alignmentClasses = {
  left: "items-start text-left",
  center: "items-center text-center",
  right: "items-end text-right",
};

export default function HeroSlider() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 8000, stopOnInteraction: true }),
  ]);
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = React.useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = React.useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section className="relative h-[calc(100vh-100px)] min-h-[500px] md:min-h-[600px] lg:min-h-[700px] w-full text-white">
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, index) => {
            const isActive = currentIndex === index;
            return (
              <div key={index} className="relative flex-[0_0_100%] h-full will-change-transform">
                <picture>
                  <source srcSet={slide.imageUrl} type="image/webp" />
                  <img
                    src={slide.imageUrl}
                    alt={slide.title?.toString() || ""}
                    className="absolute inset-0 w-full h-full object-cover"
                    decoding="async"
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "low"}
                    style={{
                      willChange: 'transform, opacity',
                      transform: 'translateZ(0)',
                      ...(index === 0 ? { objectPosition: 'center 60%' } : {}),
                      ...(index === 2 ? { objectPosition: 'center 35%' } : {}),
                      ...(index === 4 ? { objectPosition: 'center 85%' } : {}),
                    }}
                  />
                </picture>
                <div 
                  className={`absolute inset-0 ${
                    index === 0 
                      ? 'bg-gradient-to-b from-black/70 via-black/40 to-black/60' 
                      : 'bg-gradient-to-r from-black/60 via-black/35 to-transparent'
                  }`} 
                />

                <div className="container relative z-10 h-full px-5 sm:px-6">
                  <div
                    className={`h-full flex flex-col ${
                      index === 0 
                        ? 'justify-end pb-12 sm:pb-16 md:pb-20 lg:pb-24' 
                        : index === 1
                        ? 'justify-end pb-20 sm:pb-24 md:pb-28 lg:pb-40'
                        : index === 2
                        ? 'justify-end pb-20 sm:pb-24 md:pb-28 lg:pb-40'
                        : index === 3
                        ? 'justify-end pb-20 sm:pb-24 md:pb-28 lg:pb-40'
                        : 'justify-center'
                    } ${alignmentClasses[slide.align]}`}
                  >
                    <div className={`max-w-5xl ${slide.align === 'center' ? (index === 1 ? 'mx-auto -translate-x-4 sm:-translate-x-6 md:-translate-x-8' : 'mx-auto') : ''}`}>
                       <h2
                        className={`font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[72px] font-bold leading-tight text-white transition-all duration-500 ${
                          isActive
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-8"
                        }`}
                      >
                        {slide.title}
                      </h2>
                      <p
                        className={`font-body text-base sm:text-lg md:text-xl ${slide.align === 'center' ? 'max-w-4xl mx-auto text-center' : 'max-w-2xl'} mt-8 sm:mt-10 md:mt-12 leading-relaxed text-white transition-all duration-500 delay-200 ${
                          isActive
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-8"
                        }`}
                      >
                        {slide.subtitle}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-6 sm:bottom-8 md:bottom-12 lg:bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4 sm:gap-6">
        <button
          onClick={scrollPrev}
          aria-label="Previous slide"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/50 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <div className="font-body text-base sm:text-lg tracking-wider">
          <span>{String(currentIndex + 1).padStart(2, "0")}</span>
          <span className="mx-2">/</span>
          <span>{String(slides.length).padStart(2, "0")}</span>
        </div>
        <button
          onClick={scrollNext}
          aria-label="Next slide"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/50 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>
    </section>
  );
}